import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import Clickable, { ClickablePresentationProps, ClickableProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Icon from "@insite/mobius/Icon/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import Canada from "@ucs/Icons/Canada";
import Checkmark from "@ucs/Icons/Checkmark";
import UCSTree from "@ucs/Icons/UCSTree";
import USA from "@ucs/Icons/USA";
import { Groups } from "@ucs/Overrides/Widgets/Header/SecondaryNavigation";
import UCSColors from "@ucs/Utils/UCSColors";
import isMobile from "ismobilejs";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
}

export interface MenuPresentationProps {
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: boolean;
        currentCountry?: Groups;
    }
>;

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps & MenuPresentationProps & ThemeProps<BaseTheme>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.ul<MenuStyleProps>`
    background: white;
    z-index: ${getProp("theme.zIndex.menu")};
    box-shadow: ${getProp("theme.shadows.2")};
    width: ${getProp("width", 175)}px;
    list-style: none;
    position: absolute;
    padding: 15px 0px;
    margin: 0;
    display: none;
    font-size: 18px;
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen: boolean; isOpen?: boolean } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    position: relative; /* stylelint-disable */
    ${({ isOpen }) =>
        isOpen &&
        `& > ${MenuStyle} {
                display: block;
            }
    `}
    ${({ canBeOpen, isOpen }) =>
        canBeOpen &&
        isOpen &&
        `
    &:focus-within > ${MenuStyle}  /* stylelint-enable */ {
        display: block;
    }
    .trigger:focus + ${MenuStyle},
    &:hover > ${MenuStyle},
    &.focus-within > ${MenuStyle} {
        display: block;
    }`}
    ${injectCss}
`;

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    border-bottom: 1px solid ${UCSColors.gray08};
    &:first-child {
        border-top: 1px solid ${UCSColors.gray08};
    }
    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }
    a {
        width: 100%;
        min-height: 42px;
        justify-content: space-between;
    }
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean }>`
    padding: 15px 20px 15px 10px;
    color: ${getColor("text.main")};
    display: block;
    box-sizing: border-box;
    ${({ hasChildren }: { hasChildren?: boolean }) => hasChildren && "width: calc(100% - 24px);"}
`;

export interface LocationMenuStyles {
    iconTextWrapper: any;
    icon: any;
    topMenuItem: any;
    menuItem: any;
    topMenuSpacing: any;
    menuSpacing: any;
    checkmarkIcon: any;
    menuItemWithCheckMark: any;
    highLightMenuItem: any;
}

export const locationMenuStyles: LocationMenuStyles = {
    highLightMenuItem: {
        css: css`
            li:hover {
                background-color: ${UCSColors.gray09};
                span {
                    color: ${UCSColors.gray00};
                    font-weight: bold;
                }
            }
        `,
    },

    iconTextWrapper: {
        css: css`
            display: inline-flex;
            justify-content: center;
            align-items: center;
            margin-left: 20px;
        `,
    },
    icon: {
        css: css`
            margin-right: 8px;
        `,
    },
    checkmarkIcon: {
        color: UCSColors.green00,
        size: 20,
        css: css``,
    },
    topMenuSpacing: {
        css: css`
            padding-top: 8px;
            padding-bottom: 8px;
        `,
    },
    menuSpacing: {
        css: css`
            padding-top: 4px;
            padding-bottom: 4px;
        `,
    },
    topMenuItem: {
        variant: "h5",
        css: css`
            font-style: normal;
            text-transform: uppercase;
            color: ${UCSColors.gray03} !important;
            font-size: 13px;
            line-height: 1rem;
            font-weight: 800;
            letter-spacing: 10%;
            display: flex;
            flex: 1;
            justify-self: center;
            align-self: center;
            svg {
            }
            &:hover {
                background: ${UCSColors.gray09} !important;
            }
        `,
    },
    menuItemWithCheckMark: {
        css: css`
            text-transform: none !important;
            font-style: normal;
            color: ${UCSColors.gray03} !important;
            font-size: 18px;
            line-height: 18px;
            font-weight: bold !important;
            margin-left: 12px;
            letter-spacing: 10%;
        `,
    },
    menuItem: {
        css: css`
            text-transform: none !important;
            font-style: normal;
            color: ${UCSColors.gray03} !important;
            font-size: 18px;
            line-height: 18px;
            font-weight: 400;
            letter-spacing: 10%;
            margin-left: 32px;
            text-transform: none;
            display: flex;
            flex: 1;
            justify-self: center;
            align-self: center;
            svg {
            }
            &:hover {
                background: ${UCSColors.gray09} !important;
            }
        `,
    },
};

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    private locationCookie = "";
    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 175,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.locationCookie = getCookie("SelectedLocation") || "";
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, currentCountry, ...otherProps } = this.props;
                    const { applyProp, spreadProps } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );

                    const renderMenuItems = (childMenuItems: any[], currentDepth: number) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;

                        // Reorder menu items based on the selected location
                        const reorderedMenuItems = [...childMenuItems];
                        if (currentCountry === Groups.USA) {
                            const canadaIndex = reorderedMenuItems.findIndex(item => item.title === Groups.Canada);
                            if (canadaIndex !== -1) {
                                const [canadaItem] = reorderedMenuItems.splice(canadaIndex, 1);
                                reorderedMenuItems.push(canadaItem);
                            }
                        }

                        return (
                            <>
                                <MenuStyle width={otherProps.width} css={cssOverrides?.menu}>
                                    {reorderedMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, idx) => {
                                            const hasChildren =
                                                item.children?.some(
                                                    (o: { excludeFromNavigation: any }) => !o.excludeFromNavigation,
                                                ) && currentDepth < maxDepth;

                                            const isCanadaLogo: boolean = item.title === Groups.Canada;
                                            const isUCSLogo: boolean = item.title === Groups.UCS;
                                            const isUSALogo: boolean = item.title === Groups.USA;
                                            const logosValid: boolean = isCanadaLogo || isUCSLogo || isUSALogo;
                                            const topOrBottom = logosValid
                                                ? { ...locationMenuStyles.topMenuSpacing }
                                                : { ...locationMenuStyles.menuSpacing };
                                            this.locationCookie = getCookie("SelectedLocation") || "";
                                            const locationCookieSet: boolean =
                                                !logosValid &&
                                                this.locationCookie.toLowerCase() === String(item.title).toLowerCase();
                                            const menuItemStyling = locationCookieSet
                                                ? { ...locationMenuStyles.menuItemWithCheckMark }
                                                : { ...locationMenuStyles.menuItem };
                                            if (this.state.shouldRender) {
                                                return (
                                                    <StyledWrapper {...locationMenuStyles.highLightMenuItem} key={idx}>
                                                        <MenuItemStyle
                                                            width={otherProps.width}
                                                            {...topOrBottom}
                                                            data-test-selector="menuItem"
                                                            key={item.id}
                                                            onClick={() => {
                                                                if (
                                                                    (currentCountry === Groups.Canada &&
                                                                        !isCanadaLogo) ||
                                                                    (currentCountry === Groups.USA && !isUSALogo)
                                                                ) {
                                                                    this.closeMenuWithoutChildren(hasChildren);
                                                                }
                                                            }}
                                                        >
                                                            <Clickable
                                                                href={item.url.length > 0 ? item.url : ""}
                                                                style={
                                                                    !item.url && (isCanadaLogo || isUSALogo)
                                                                        ? { cursor: "default" }
                                                                        : { cursor: "pointer" }
                                                                }
                                                                onClick={(
                                                                    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
                                                                ) => {
                                                                    if (
                                                                        (currentCountry === Groups.Canada &&
                                                                            !isCanadaLogo) ||
                                                                        (currentCountry === Groups.USA && !isUSALogo)
                                                                    ) {
                                                                        // Call back to secondary navigation
                                                                        if (item.updateSelectedLocation) {
                                                                            item.updateSelectedLocation(item.id);
                                                                        }
                                                                        if (item.url.length === 0) {
                                                                            logger.warn(
                                                                                `An attempt to redirect from ${currentCountry} site failed because ${
                                                                                    isCanadaLogo ? "CA" : "US"
                                                                                } URL was not configured in Site Messages. Please set a valid URL for ${
                                                                                    isCanadaLogo
                                                                                        ? "Site_Url_CA"
                                                                                        : "Site_Url_US"
                                                                                }.`,
                                                                            );
                                                                        }

                                                                        this.closeMenu();
                                                                        if (
                                                                            item.clickableProps &&
                                                                            item.clickableProps.onClick
                                                                        ) {
                                                                            item.clickableProps.onClick(e);
                                                                        }
                                                                    }
                                                                }}
                                                                {...applyProp("menuItemClickableProps")}
                                                            >
                                                                <StyledWrapper {...locationMenuStyles.iconTextWrapper}>
                                                                    {isCanadaLogo && (
                                                                        <Icon
                                                                            {...locationMenuStyles.icon}
                                                                            src={Canada}
                                                                        />
                                                                    )}
                                                                    {isUCSLogo && (
                                                                        <Icon
                                                                            {...locationMenuStyles.icon}
                                                                            src={UCSTree}
                                                                        />
                                                                    )}
                                                                    {isUSALogo && (
                                                                        <Icon {...locationMenuStyles.icon} src={USA} />
                                                                    )}

                                                                    {/* Handling adding the check mark and bold the text. */}
                                                                    {locationCookieSet && (
                                                                        <Icon
                                                                            {...locationMenuStyles.checkmarkIcon}
                                                                            src={Checkmark}
                                                                        />
                                                                    )}

                                                                    {logosValid ? (
                                                                        <MenuItemText
                                                                            hasChildren={hasChildren}
                                                                            {...applyProp("menuItemTypographyProps")}
                                                                            {...locationMenuStyles.topMenuItem}
                                                                        >
                                                                            {item.title}
                                                                        </MenuItemText>
                                                                    ) : (
                                                                        <MenuItemText
                                                                            hasChildren={hasChildren}
                                                                            {...applyProp("menuItemTypographyProps")}
                                                                            {...menuItemStyling}
                                                                        >
                                                                            {item.title}
                                                                        </MenuItemText>
                                                                    )}
                                                                </StyledWrapper>
                                                            </Clickable>
                                                            {hasChildren
                                                                ? renderMenuItems(item!.children!, currentDepth + 1)
                                                                : null}
                                                        </MenuItemStyle>
                                                    </StyledWrapper>
                                                );
                                            }
                                            return null;
                                        })}
                                </MenuStyle>
                            </>
                        );
                    };

                    const cssOverrides = spreadProps("cssOverrides");
                    const descriptionId = otherProps.descriptionId || uniqueId();

                    return (
                        <>
                            <MenuWrapper
                                canBeOpen={this.state.canBeOpen}
                                isOpen={isOpen}
                                css={cssOverrides?.wrapper}
                                {...{ "aria-labelledby": descriptionId }}
                                {...otherProps}
                            >
                                {React.cloneElement(menuTrigger, { id: descriptionId, className: "trigger" })}
                                {renderMenuItems(menuItems, 1)}
                            </MenuWrapper>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default withTheme(Menu);

export { MenuItemText };
